/**
 * Utilidades compartidas de Zen
 */
import { formatDate, formatDateTime } from './locale'

/** Formatea una fecha ISO YYYY-MM-DD según locale (default es-CL → dd-mm-yyyy) */
export function fmtDate(iso: string | null | undefined, locale = 'es-CL'): string {
  return formatDate(iso, locale)
}

/** Formatea un RUT chileno sin puntos (97004000-5) a formato con puntos (97.004.000-5) */
export function formatRut(rut: string | null | undefined): string {
  if (!rut) return '—'
  const clean = rut.replace(/\./g, '').replace(/-/g, '').trim().toUpperCase()
  if (clean.length < 2) return rut
  const body = clean.slice(0, -1)
  const dv = clean.slice(-1)
  const formatted = body.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  return `${formatted}-${dv}`
}

/** Formatea un datetime ISO según locale (default es-CL → dd-mm-yyyy HH:MM) */
export function fmtDateTime(iso: string | null | undefined, locale = 'es-CL'): string {
  return formatDateTime(iso, locale)
}

function _fmtLocalDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/**
 * Hoy, en hora LOCAL, como `YYYY-MM-DD`: el valor por defecto de un campo de fecha.
 *
 * 🔴 NO usar `new Date().toISOString().slice(0, 10)`: eso es la fecha en UTC, y en
 * Chile (UTC-4/-3) desde las 20:00 ya es el día siguiente. Un pago registrado de
 * noche nacía con la fecha de mañana. Había 38 lugares así (2026-09-29).
 */
export function todayISO(): string {
  return _fmtLocalDate(new Date())
}

/**
 * Rango del periodo actual (mes en curso): primer día del mes → hoy, en ISO
 * `YYYY-MM-DD`. Mismo cálculo que usa el dashboard, para que las listas con
 * filtro de fecha no traigan todo el histórico por defecto.
 *
 * 🔴 La fecha sale de `_fmtLocalDate` y NO de `toISOString()`, que devuelve UTC.
 * En Chile (UTC-4) desde las 20:00 el día UTC ya es el siguiente, así que la
 * noche del último día del mes el rango saltaba al mes entrante y quedaba
 * `from = to = <día 1 del mes que viene>`: los listados de ventas, compras e
 * invoices internacionales se veían VACÍOS. Medido en prod el 2026-08-31 a las
 * 21:13 de Chile → `date_from=2026-09-01&date_to=2026-09-01` con 3.551
 * documentos en la base. El resto del mes no se nota, porque el rango solo se
 * pasa un día hacia adelante y sigue conteniendo todo lo emitido.
 */
export function currentMonthRange(): { from: string; to: string } {
  const today = _fmtLocalDate(new Date())
  const from = today.substring(0, 7) + '-01'
  return { from, to: today }
}

/** Rango del mes anterior (primer→último día). */
export function lastMonthRange(): { from: string; to: string } {
  const now = new Date()
  const firstThisMonth = new Date(now.getFullYear(), now.getMonth(), 1)
  const lastPrev = new Date(firstThisMonth.getTime() - 86400000)
  const firstPrev = new Date(lastPrev.getFullYear(), lastPrev.getMonth(), 1)
  return { from: _fmtLocalDate(firstPrev), to: _fmtLocalDate(lastPrev) }
}

/** Rango del año en curso (1 de enero → hoy). */
export function currentYearRange(): { from: string; to: string } {
  const now = new Date()
  return { from: `${now.getFullYear()}-01-01`, to: _fmtLocalDate(now) }
}
